import React, { useState, useEffect } from "react";
import { eventsData } from "../../Components/Events/eventsData";
import { useNavigate, Link, useLocation, useParams } from "react-router-dom";
import { PhoneIcon, ChevronDownIcon } from "@heroicons/react/outline"; // Import the PhoneIcon
import { useAuth } from "../../Contexts/AuthContext";

import {
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
} from "firebase/auth";
import { auth } from "../../Services/firebase-config";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../Services/firebase-config.js";
import { convertDeadlineToDate } from "../../Components/helpers/helpers.js";

// Import your DeadlineModal component
// import DeadlineModal from "path/to/DeadlineModal";

// Districts array for Uganda
const UGANDA_DISTRICTS = [
  "Abim",
  "Adjumani",
  "Agago",
  "Alebtong",
  "Amolatar",
  "Amudat",
  "Amuria",
  "Amuru",
  "Apac",
  "Arua",
  "Budaka",
  "Bududa",
  "Bugiri",
  "Buhweju",
  "Buikwe",
  "Bukedea",
  "Bukomansimbi",
  "Bukwo",
  "Bulambuli",
  "Buliisa",
  "Bundibugyo",
  "Bushenyi",
  "Busia",
  "Butaleja",
  "Butambala",
  "Buvuma",
  "Buyende",
  "Dokolo",
  "Gomba",
  "Gulu",
  "Hoima",
  "Ibanda",
  "Iganga",
  "Isingiro",
  "Jinja",
  "Kaabong",
  "Kabale",
  "Kabarole",
  "Kaberamaido",
  "Kalangala",
  "Kaliro",
  "Kampala",
  "Kamuli",
  "Kamwenge",
  "Kanungu",
  "Kapchorwa",
  "Kasese",
  "Katakwi",
  "Kayunga",
  "Kibaale",
  "Kiboga",
  "Kibuku",
  "Kiruhura",
  "Kiryandongo",
  "Kisoro",
  "Kitgum",
  "Koboko",
  "Kole",
  "Kotido",
  "Kumi",
  "Kween",
  "Kyankwanzi",
  "Kyegegwa",
  "Kyenjojo",
  "Lamwo",
  "Lira",
  "Luuka",
  "Luwero",
  "Lwengo",
  "Lyantonde",
  "Manafwa",
  "Maracha",
  "Masaka",
  "Masindi",
  "Mayuge",
  "Mbale",
  "Mbarara",
  "Mitooma",
  "Mityana",
  "Moroto",
  "Moyo",
  "Mpigi",
  "Mubende",
  "Mukono",
  "Nakapiripirit",
  "Nakaseke",
  "Nakasongola",
  "Namayingo",
  "Namutumba",
  "Napak",
  "Nebbi",
  "Ngora",
  "Ntoroko",
  "Ntungamo",
  "Nwoya",
  "Otuke",
  "Oyam",
  "Pader",
  "Pallisa",
  "Rakai",
  "Rubirizi",
  "Rukungiri",
  "Sembabule",
  "Serere",
  "Sheema",
  "Sironko",
  "Soroti",
  "Tororo",
  "Wakiso",
  "Yumbe",
  "Zombo",
];

const SignupPage = () => {
  const { currentUser, userData } = useAuth();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [institutionName, setInstitutionName] = useState("");
  const [gender, setGender] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [region, setRegion] = useState("");
  const [regionSearch, setRegionSearch] = useState("");
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);
  const [error, setError] = useState(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  // Get event parameters from both URL params and query string
  const isEvent =
    params.event === "true" ||
    new URLSearchParams(location.search).get("event") === "true" ||
    location.state?.isEvent === true;
  const eventId =
    params.eventId ||
    new URLSearchParams(location.search).get("eventId") ||
    location.state?.eventId ||
    "a1b2c3d4-e5f6-7890-abcd-ef1234567890";

  // Check target event deadline
  const targetEvent = eventsData.find((e) => e.id === eventId);

  const deadlineTimestamp = convertDeadlineToDate(targetEvent?.deadline);
  const isRegistrationClosed =
    deadlineTimestamp && Date.now() > deadlineTimestamp;

  // Format deadline for display
  const formatDeadlineDisplay = () => {
    if (!deadlineTimestamp) return "";

    return new Date(deadlineTimestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Show deadline modal when registration is closed
  // The fallback modal below will handle display

  const closeModal = () => {
    navigate("/"); // Redirect to home page after closing modal
  };

  useEffect(() => {
    // Store event parameters in local storage for potential use after redirect
    if (isEvent && eventId) {
      localStorage.setItem("eventRedirect", "true");
      localStorage.setItem("eventId", eventId);
    }
  }, [isEvent, eventId]);

  // Redirect if user is already logged in
  useEffect(() => {
    if (currentUser && userData) {
      // User is already logged in, redirect based on role
      if (userData?.role === "Administrator") {
        navigate("/eventdashboard/dashboard");
      } else if (userData?.role === "Judge") {
        navigate("/eventdashboard/manage-applications");
      } else if (userData?.role === "Mentor") {
        navigate("/eventdashboard/teams-management");
      } else if (isEvent && eventId) {
        // Regular user in event context
        navigate(`/hackathon-dashboard?events=true&eventId=${eventId}`);
      } else {
        // Regular user, redirect to home or appropriate page
        const from = location.state?.from?.pathname || "/";
        navigate(from);
      }
    }
  }, [currentUser, userData, navigate, isEvent, eventId, location.state]);

  // Close region dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".region-dropdown-container")) {
        setShowRegionDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async (event) => {
    setError(null);
    setIsLoggingIn(true);
    event.preventDefault();

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      await updateProfile(userCredential.user, {
        displayName: fullName,
      });

      const userRef = doc(db, "users", userCredential.user.uid);
      await setDoc(
        userRef,
        {
          lastAccessed: new Date(),
          displayName: fullName,
          name: fullName,
          role: "Applicant", // Default role for new users
          email: email,
          phoneNumber: phoneNumber,
          institutionName: institutionName,
          gender: gender,
          ageRange: ageRange,
          region: region,
          joinedAt: new Date(),
        },
        { merge: true },
      );

      await sendEmailVerification(userCredential.user);

      // Determine redirect path based on event parameters
      if (isEvent) {
        // If from an event, redirect to verify email, but save event info
        navigate("/verify-email", {
          state: {
            isEvent: true,
            eventId: eventId,
          },
        });
      } else {
        // Standard redirect
        navigate("/verify-email");
      }
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        setError("The email address is already in use by another account.");
      } else if (error.code === "auth/weak-password") {
        setError("Password should be at least 6 characters");
      } else {
        setError("Something went wrong. Please try again later.");
      }
      console.error("Error signing up: ", error);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const togglePopup = () => {
    setIsPopupVisible(!isPopupVisible);
  };

  // Filter districts based on search input
  const filteredDistricts = UGANDA_DISTRICTS.filter((district) =>
    district.toLowerCase().includes(regionSearch.toLowerCase()),
  );

  // Handle region selection
  const handleRegionSelect = (district) => {
    setRegion(district);
    setRegionSearch(district);
    setShowRegionDropdown(false);
  };

  // Handle region search input
  const handleRegionSearchChange = (e) => {
    const value = e.target.value;
    setRegionSearch(value);
    setRegion(value);
    setShowRegionDropdown(true);
  };

  // Generate the appropriate login link based on event context
  const getLoginLink = () => {
    if (isEvent && eventId) {
      return `/login?isEvent=${isEvent}&eventId=${eventId}`;
    }
    return "/login";
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center flex flex-col justify-center items-center py-8 px-4"
      style={{ backgroundImage: `url('/Images/background.jpg')` }}
    >
      {/* Use DeadlineModal component */}
      {/* Uncomment when you have the DeadlineModal component imported */}
      {/* 
      <DeadlineModal 
        isOpen={isDeadlineModalOpen} 
        event={targetEvent} 
        deadline={formatDeadlineDisplay()} 
        onClose={closeModal} 
      />
      */}

      {/* Fallback blocking modal when registration for the target event is closed */}
      {/* Only show when user is registering for a specific event */}
      {isEvent && isRegistrationClosed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-xl p-8 max-w-md mx-4 text-center shadow-2xl">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
                <path
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v4"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Registration Closed
            </h3>
            <p className="text-gray-600 mb-6">
              The registration for <strong>{targetEvent?.title}</strong> has
              closed. You can no longer register for this event.
            </p>
            <div className="text-sm text-gray-500 mb-6">
              Deadline was: <strong>{formatDeadlineDisplay() || "--"}</strong>
            </div>
            <div className="flex gap-2">
              <button
                onClick={closeModal}
                className="w-full py-3 px-6 bg-custom-blue-1 hover:bg-custom-blue-1/90 text-white font-semibold rounded-lg transition-colors"
              >
                Back to Events
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl w-full bg-custom-blue-1 p-6 sm:p-8 border shadow my-4">
        <div className="flex justify-center mb-6 sm:mb-8">
          <img
            className="w-16 sm:w-20"
            src={"/Images/STIlogo.svg"}
            alt="STI  Logo"
          />
        </div>
        <h2 className="text-xl sm:text-2xl font-normal mb-2 text-white text-center">
          REGISTER TO CREATE ACCOUNT
        </h2>
        <h3 className="text-sm font-medium mb-6 sm:mb-8 text-white text-center">
          Welcome to the STI Events Platform!
        </h3>
        <h3 className="text-sm font-small mb-6 sm:mb-8 text-white text-center">
          Create an account to get started with your application and participate
          in the latest call for innovations.
        </h3>

        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-800 rounded">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {/* Row 1: Full Name and Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label
                htmlFor="fullName"
                className="block text-sm text-white font-semibold mb-2"
              >
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                className="shadow-sm bg-gray-50 py-2 px-4 block w-full"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm text-white font-semibold mb-2"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="shadow-sm bg-gray-50 py-2 px-4 block w-full"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Row 2: Password and Phone Number */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label
                htmlFor="password"
                className="block text-sm text-white font-semibold mb-2"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                className="shadow-sm bg-gray-50 py-2 px-4 block w-full"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm text-white font-semibold mb-2"
              >
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                className="shadow-sm bg-gray-50 py-2 px-4 block w-full"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+256"
                required
              />
            </div>
          </div>

          {/* Row 3: Gender and Region */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label
                htmlFor="gender"
                className="block text-sm text-white font-semibold mb-2"
              >
                Gender
              </label>
              <select
                id="gender"
                name="gender"
                className="shadow-sm bg-gray-50 py-2 px-4 block w-full"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                required
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div className="relative region-dropdown-container">
              <label
                htmlFor="region"
                className="block text-sm text-white font-semibold mb-2"
              >
                Region/District
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="region"
                  name="region"
                  className="shadow-sm bg-gray-50 py-2 px-4 pr-10 block w-full"
                  value={regionSearch}
                  onChange={handleRegionSearchChange}
                  onFocus={() => setShowRegionDropdown(true)}
                  placeholder="Search for your district..."
                  required
                />
                <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>

              {/* Dropdown */}
              {showRegionDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {filteredDistricts.length > 0 ? (
                    filteredDistricts.map((district) => (
                      <div
                        key={district}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-900"
                        onClick={() => handleRegionSelect(district)}
                      >
                        {district}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-gray-500">
                      No districts found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Row 4: Age Range and Institution Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label
                htmlFor="ageRange"
                className="block text-sm text-white font-semibold mb-2"
              >
                Age Range
              </label>
              <select
                id="ageRange"
                name="ageRange"
                className="shadow-sm bg-gray-50 py-2 px-4 block w-full"
                value={ageRange}
                onChange={(e) => setAgeRange(e.target.value)}
                required
              >
                <option value="">Select Age Range</option>
                <option value="18-24">18-24</option>
                <option value="25-34">25-34</option>
                <option value="35-44">35-44</option>
                <option value="45-54">45-54</option>
                <option value="55+">55+</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="institutionName"
                className="block text-sm text-white font-semibold mb-2"
              >
                Institution Name
              </label>
              <input
                type="text"
                id="institutionName"
                name="institutionName"
                className="shadow-sm bg-gray-50 py-2 px-4 block w-full"
                value={institutionName}
                onChange={(e) => setInstitutionName(e.target.value)}
                placeholder="Enter your institution name"
                required
              />
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            {/* Already have an account */}
            <div className="text-sm">
              <Link
                to={getLoginLink()}
                className="font-small text-white hover:text-custom-green-1"
              >
                Already have an account?{" "}
                <span className="font-bold">LOGIN</span>
              </Link>
            </div>

            {/* Need Assistance Section */}
            <div className="text-sm">
              <button
                type="button" // Set to button to prevent default form submission behavior
                className="text-white text-sm font-semibold hover:underline"
                onClick={togglePopup}
              >
                Need Assistance?
              </button>
            </div>

            {/* Popup */}
            {isPopupVisible && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-6 shadow-md max-w-sm w-full">
                  <h3 className="text-lg font-bold mb-4">Need Assistance?</h3>
                  <p className="text-gray-700 mb-4">
                    You can reach out to us using the following phone numbers:
                  </p>
                  <p className="flex items-center mb-2">
                    <PhoneIcon className="h-5 w-5 text-gray-500 mr-2" />
                    <span>+256773308792</span>
                  </p>
                  <p className="flex items-center mb-4">
                    <PhoneIcon className="h-5 w-5 text-gray-500 mr-2" />
                    <span>+256701822708</span>
                  </p>
                  <button
                    className="bg-custom-blue-1 text-white py-2 px-4 hover:bg-blue-600 transition duration-300 ease-in-out w-full"
                    onClick={togglePopup}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-custom-green-1 text-custom-blue-1 py-2 px-4 border border-transparent hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingIn ? "Creating Account..." : "Sign Up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupPage;
